/**
 * Hook React pour la déconnexion sécurisée.
 * 
 * Wrape `logout()` avec gestion d'état (loading, error)
 * et callbacks (onSuccess, onError) pour une intégration UX simple.
 * 
 * @example
 * ```tsx
 * import { useLogout } from '@ollaid/native-sso';
 * 
 * const LogoutButton = () => {
 *   const { logout, loading, error } = useLogout({
 *     onSuccess: () => navigate('/auth/login'),
 *     onError: (err) => toast.error(err.message),
 *   });
 * 
 *   return (
 *     <button onClick={logout} disabled={loading}>
 *       {loading ? 'Déconnexion...' : 'Se déconnecter'}
 *     </button>
 *   );
 * };
 * ```
 * 
 * @version 2.7.9
 */

import { useState, useCallback } from 'react';
import { logout } from '../services/api';

export interface UseLogoutOptions {
  /** Callback appelé après une déconnexion réussie (redirection, toast, etc.) */
  onSuccess?: () => void;
  /** Callback appelé en cas d'erreur (notification, log, etc.) */
  onError?: (error: Error) => void;
}

export interface UseLogoutReturn {
  /** Déclenche la déconnexion complète (double revocation SaaS + IAM) */
  logout: () => Promise<void>;
  /** `true` pendant l'appel de déconnexion */
  loading: boolean;
  /** Message d'erreur si la déconnexion a échoué, `null` sinon */
  error: string | null;
}

export const useLogout = (options?: UseLogoutOptions): UseLogoutReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogout = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await logout();
      options?.onSuccess?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de déconnexion';
      setError(message);
      options?.onError?.(err instanceof Error ? err : new Error(message));
    } finally {
      setLoading(false);
    }
  }, [options]);

  return { logout: handleLogout, loading, error };
};
